import React, { useState, useRef } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonItem,
  IonLabel,
  IonCard,
  IonCardContent,
  IonIcon,
  IonToast,
  IonLoading,
  IonButtons,
  IonGrid,
  IonRow,
  IonCol,
  IonImg,
  IonActionSheet,
} from "@ionic/react";
import {
  cameraOutline,
  imageOutline,
  trashOutline,
  sendOutline,
  arrowBack,
  cloudUploadOutline,
  documentTextOutline,
  listOutline,
  flagOutline,
  chatboxOutline,
} from "ionicons/icons";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { useTranslation } from "react-i18next";
import { ApiService } from "../services/api.service";
import "./SubmitReport.css";

// Camera types and module handling
interface CameraPhoto {
  dataUrl?: string;
  format: string;
}

// Simple camera fallback function
const getCameraModule = () => {
  // For now, always return null to use web file input
  // In a real mobile app, this would check for native camera availability
  return null;
};

interface SubmitReportProps {
  onBack: () => void;
  onSubmitSuccess: () => void;
}

export interface ReportFormData {
  title: string;
  issueType: string;
  description: string;
  priority: string;
  images: string[];
  location?: {
    latitude: number;
    longitude: number;
  };
  userId: number;
}

interface UserLocation {
  latitude: number;
  longitude: number;
}

const SubmitReport: React.FC<SubmitReportProps> = ({
  onBack,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState<
    Omit<ReportFormData, "location" | "userDetails">
  >({
    title: "",
    issueType: "",
    description: "",
    priority: "medium",
    images: [],
    userId: 0, // This will be set later
  });

  // Retrieve user info from localStorage
  const [userIdentifier, setUserIdentifier] = useState<string>("");
  const [userId, setUserId] = useState<number>(0);

  // Load user data from localStorage on component mount
  React.useEffect(() => {
    try {
      const storedUser = localStorage.getItem("userData");
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUserIdentifier(userData.emailOrMobile);
        setUserId(userData.userId || 0);
      }
    } catch (error) {
      console.error("Failed to load user data from localStorage:", error);
    }
  }, []);

  const [location, setLocation] = useState<UserLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  // Removed isGettingLocation since location capture is now automatic

  // Validation errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = t("submitReport.validation.titleRequired");
    }
    if (!formData.issueType) {
      newErrors.issueType = t("submitReport.validation.issueTypeRequired");
    }
    if (!formData.description.trim()) {
      newErrors.description = t("submitReport.validation.descriptionRequired");
    }
    if (formData.images.length === 0) {
      newErrors.images = t("submitReport.validation.imageRequired");
    }
    // User details validation removed since they're now hidden and auto-populated

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const takePhoto = async () => {
    try {
      const cameraModule = getCameraModule();
      if (cameraModule) {
        // This would be used for native mobile camera
        // Currently not implemented to avoid compilation issues
      } else {
        // Web fallback - trigger file input
        if (fileInputRef.current) {
          fileInputRef.current.accept = "image/*";
          fileInputRef.current.capture = "environment";
          fileInputRef.current.click();
        }
      }
    } catch (error) {
      console.error("Error taking photo:", error);
      setToastMessage(t("submitReport.errors.photoFailed"));
      setShowToast(true);
    }
    setIsActionSheetOpen(false);
  };

  const selectFromGallery = async () => {
    try {
      const cameraModule = getCameraModule();
      if (cameraModule) {
        // This would be used for native mobile photo gallery
        // Currently not implemented to avoid compilation issues
      } else {
        // Web fallback - trigger file input
        if (fileInputRef.current) {
          fileInputRef.current.accept = "image/*";
          fileInputRef.current.capture = "";
          fileInputRef.current.click();
        }
      }
    } catch (error) {
      console.error("Error selecting photo:", error);
      setToastMessage(t("submitReport.errors.photoFailed"));
      setShowToast(true);
    }
    setIsActionSheetOpen(false);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const currentImageCount = formData.images.length;
      const maxImages = 3;
      const remainingSlots = maxImages - currentImageCount;

      if (remainingSlots <= 0) {
        setToastMessage(
          t("submitReport.errors.maxImagesReached", { max: maxImages })
        );
        setShowToast(true);
        return;
      }

      const filesToProcess = Array.from(files).slice(0, remainingSlots);

      if (files.length > remainingSlots) {
        setToastMessage(
          t("submitReport.errors.tooManyImages", {
            max: maxImages,
            remaining: remainingSlots,
          })
        );
        setShowToast(true);
      }

      filesToProcess.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, dataUrl],
          }));
          // Clear image error when user adds an image
          if (errors.images) {
            setErrors((prev) => ({ ...prev, images: "" }));
          }
        };
        reader.readAsDataURL(file);
      });
    }

    // Reset the input value to allow selecting the same file again if needed
    event.target.value = "";
  };

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const getCurrentLocation = async (): Promise<UserLocation | null> => {
    try {
      const coordinates = await Geolocation.getCurrentPosition();
      const locationData = {
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
      };
      setLocation(locationData);
      return locationData;
    } catch (error) {
      console.error("Error getting location:", error);
      // Don't show toast here since location capture is now silent
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setToastMessage(t("submitReport.errors.validationFailed"));
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    try {
      // Automatically capture location during submission
      const currentLocation = await getCurrentLocation();

      const submissionData: ReportFormData = {
        ...formData,
        location: currentLocation || undefined,
        userId,
      };

      const response = await ApiService.submitReport(submissionData);

      setToastMessage(t("submitReport.success.submitted"));
      setShowToast(true);

      // Reset form
      setFormData({
        title: "",
        issueType: "",
        description: "",
        priority: "medium",
        images: [],
        userId: userId,
      });
      // User details remain the same since they're tied to logged-in user
      setLocation(null);
      setErrors({});

      // Call success callback after a delay
      setTimeout(() => {
        onSubmitSuccess();
      }, 2000);
    } catch (error) {
      console.error("Error submitting report:", error);
      setToastMessage(t("submitReport.errors.submitFailed"));
      setShowToast(true);
    }
    setIsLoading(false);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onBack}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>{t("submitReport.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="submit-report-content">
        <div className="submit-report-container">
          {/* Report Details Section */}
          <IonCard>
            <IonCardContent>
              <IonItem className={errors.title ? "error" : ""}>
                <IonLabel position="stacked">
                  <IonIcon
                    icon={documentTextOutline}
                    slot="start"
                    color="primary"
                  />
                  {t("submitReport.fields.title")} *
                </IonLabel>
                <IonInput
                  value={formData.title}
                  placeholder={t("submitReport.placeholders.title")}
                  onIonInput={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      title: e.detail.value!,
                    }));
                    // Clear error when user starts typing
                    if (errors.title) {
                      setErrors((prev) => ({ ...prev, title: "" }));
                    }
                  }}
                />
              </IonItem>
              {errors.title && (
                <div className="error-message">{errors.title}</div>
              )}

              <IonItem className={errors.issueType ? "error" : ""}>
                <IonLabel position="stacked">
                  <IonIcon icon={listOutline} slot="start" color="primary" />
                  {t("submitReport.fields.issueType")} *
                </IonLabel>
                <IonSelect
                  value={formData.issueType}
                  placeholder={t("submitReport.placeholders.issueType")}
                  onIonChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      issueType: e.detail.value,
                    }));
                    // Clear error when user selects
                    if (errors.issueType) {
                      setErrors((prev) => ({ ...prev, issueType: "" }));
                    }
                  }}
                >
                  <IonSelectOption value="pothole">
                    {t("issueTypes.pothole")}
                  </IonSelectOption>
                  <IonSelectOption value="streetlight">
                    {t("issueTypes.streetlight")}
                  </IonSelectOption>
                  <IonSelectOption value="drainage">
                    {t("issueTypes.drainage")}
                  </IonSelectOption>
                  <IonSelectOption value="waste">
                    {t("issueTypes.waste")}
                  </IonSelectOption>
                  <IonSelectOption value="traffic">
                    {t("issueTypes.traffic")}
                  </IonSelectOption>
                  <IonSelectOption value="accident">
                    {t("issueTypes.accident")}
                  </IonSelectOption>
                  <IonSelectOption value="roadwork">
                    {t("issueTypes.roadwork")}
                  </IonSelectOption>
                  <IonSelectOption value="flooding">
                    {t("issueTypes.flooding")}
                  </IonSelectOption>
                  <IonSelectOption value="illegal-dumping">
                    {t("issueTypes.illegal-dumping")}
                  </IonSelectOption>
                  <IonSelectOption value="damagedroad">
                    {t("issueTypes.damagedroad")}
                  </IonSelectOption>
                  <IonSelectOption value="other">
                    {t("issueTypes.other")}
                  </IonSelectOption>
                </IonSelect>
              </IonItem>
              {errors.issueType && (
                <div className="error-message">{errors.issueType}</div>
              )}

              <IonItem>
                <IonLabel position="stacked">
                  <IonIcon icon={flagOutline} slot="start" color="primary" />
                  {t("submitReport.fields.priority")}
                </IonLabel>
                <IonSelect
                  value={formData.priority}
                  onIonChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: e.detail.value,
                    }))
                  }
                >
                  <IonSelectOption value="low">
                    {t("submitReport.priority.low")}
                  </IonSelectOption>
                  <IonSelectOption value="medium">
                    {t("submitReport.priority.medium")}
                  </IonSelectOption>
                  <IonSelectOption value="high">
                    {t("submitReport.priority.high")}
                  </IonSelectOption>
                </IonSelect>
              </IonItem>

              <IonItem className={errors.description ? "error" : ""}>
                <IonLabel position="stacked">
                  <IonIcon icon={chatboxOutline} slot="start" color="primary" />
                  {t("submitReport.fields.description")} *
                </IonLabel>
                <IonTextarea
                  value={formData.description}
                  placeholder={t("submitReport.placeholders.description")}
                  rows={4}
                  onIonInput={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      description: e.detail.value!,
                    }));
                    // Clear error when user starts typing
                    if (errors.description) {
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                />
              </IonItem>
              {errors.description && (
                <div className="error-message">{errors.description}</div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Images Section */}
          <IonCard>
            <IonCardContent>
              <h2>
                <IonIcon
                  icon={imageOutline}
                  style={{ marginRight: "8px", verticalAlign: "middle" }}
                />
                {t("submitReport.sections.images")} *
              </h2>

              <div className="upload-section">
                <IonButton
                  fill="outline"
                  onClick={() => setIsActionSheetOpen(true)}
                  className="upload-button"
                  disabled={formData.images.length >= 3}
                >
                  <IonIcon icon={cloudUploadOutline} slot="start" />
                  {formData.images.length >= 3
                    ? t("submitReport.buttons.maxImagesReached")
                    : `${t("submitReport.buttons.addImage")} (${
                        formData.images.length
                      }/3)`}
                </IonButton>

                {/* Hidden file input for web fallback */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileInput}
                />
              </div>

              {errors.images && (
                <div className="error-message">{errors.images}</div>
              )}

              {formData.images.length > 0 && (
                <div className="images-row">
                  {formData.images.map((image, index) => (
                    <div key={index} className="image-thumbnail">
                      <IonImg src={image} alt={`Report image ${index + 1}`} />
                      <IonButton
                        fill="clear"
                        color="danger"
                        size="small"
                        className="remove-thumbnail-button"
                        onClick={() => removeImage(index)}
                      >
                        <IonIcon icon={trashOutline} />
                      </IonButton>
                    </div>
                  ))}
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Submit Button */}
          <div className="submit-section">
            {/* Hidden field to track user identifier */}
            <input type="hidden" value={userIdentifier} name="userIdentifier" />

            <IonButton
              expand="block"
              onClick={handleSubmit}
              disabled={isLoading}
              className="submit-button"
            >
              <IonIcon icon={sendOutline} slot="start" />
              {isLoading
                ? t("submitReport.buttons.submitting")
                : t("submitReport.buttons.submit")}
            </IonButton>
          </div>
        </div>

        {/* Action Sheet for Image Selection */}
        <IonActionSheet
          isOpen={isActionSheetOpen}
          onDidDismiss={() => setIsActionSheetOpen(false)}
          buttons={[
            {
              text: t("submitReport.imageOptions.camera"),
              icon: cameraOutline,
              handler: takePhoto,
            },
            {
              text: t("submitReport.imageOptions.gallery"),
              icon: imageOutline,
              handler: selectFromGallery,
            },
            {
              text: t("submitReport.buttons.cancel"),
              role: "cancel",
            },
          ]}
        />

        {/* Loading */}
        <IonLoading
          isOpen={isLoading}
          message={t("submitReport.loading.submitting")}
        />

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  );
};

export default SubmitReport;
